import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import PublicSiteHeader from '@components/layout/PublicSiteHeader';
import SiteFooter from '@components/layout/SiteFooter';
import { brandColors } from '@config/brandTokens';
import LandingFoundationContestSection from '@components/landing/LandingFoundationContestSection';
import LandingConvertSection from '@components/landing/LandingConvertSection';

const HOME_MAX_WIDTH = 1440;

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicSiteHeader />

      {/* Hero — full-bleed banner; copy stays on a readable measure */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          minHeight: { xs: 420, md: 560 },
          display: 'flex',
          alignItems: 'center',
          color: 'white',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/images/Badminton-Smash-Contest.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(105deg, rgba(15,23,42,0.88) 0%, rgba(17,23,57,0.62) 46%, rgba(29,78,216,0.32) 100%)',
          }}
        />
        <Container
          maxWidth={false}
          sx={{ position: 'relative', maxWidth: HOME_MAX_WIDTH, py: { xs: 6, md: 8 }, px: { xs: 2.5, md: 4 } }}
        >
          <Stack spacing={2.25} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Sora", Arial, sans-serif',
                fontWeight: 800,
                maxWidth: { xs: '100%', md: 1100 },
                lineHeight: 1.15,
                fontSize: { xs: '1.7rem', sm: '2.2rem', md: '3.05rem' },
                hyphens: 'none',
                overflowWrap: 'normal',
                wordBreak: 'normal',
              }}
            >
              First ever unified sports platform
              <Box
                component="span"
                sx={{ display: { xs: 'inline', sm: 'block' } }}
              >
                {' '}
                with structured learning agewise
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Sora", Arial, sans-serif',
                fontWeight: 500,
                opacity: 0.94,
                maxWidth: 680,
                lineHeight: 1.55,
                fontSize: { xs: '1.08rem', md: '1.35rem' },
              }}
            >
              Rank skills. Learn deeply. Compete in contests — EncycGames brings rubrics, video learning, rankings and
              contests together for coaches, youth and parents.
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'center', md: 'flex-start' }}>
              {['Rubrics', 'Learning', 'Rankings', 'Contests', 'Academies'].map((pill) => (
                <Chip
                  key={pill}
                  label={pill}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.14)',
                    color: '#f8fafc',
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', md: '0.95rem' },
                    height: { xs: 30, md: 34 },
                    fontFamily: '"Sora", Arial, sans-serif',
                  }}
                />
              ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 0.5 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register/player')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 3.5,
                  py: 1.15,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontFamily: '"Sora", Arial, sans-serif',
                  bgcolor: brandColors.orange,
                  color: brandColors.white,
                  '&:hover': { bgcolor: '#f07a3d' },
                }}
              >
                Register free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3.5,
                  py: 1.15,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontFamily: '"Sora", Arial, sans-serif',
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Sign in
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Main content — Prize money 2027, Choose Your Sport, and Foundation discount offer hidden for 1.0 home */}
      <Container maxWidth={false} sx={{ py: { xs: 6, md: 8 }, maxWidth: HOME_MAX_WIDTH, px: { xs: 2.5, md: 4 } }}>
        <LandingFoundationContestSection />
        <LandingConvertSection />
      </Container>

      {/* Contact strip */}
      <Box sx={{ bgcolor: brandColors.ice, py: { xs: 6, md: 7 }, borderTop: '1px solid #e2e8f0', flex: 1 }}>
        <Container maxWidth={false} sx={{ textAlign: 'center', maxWidth: HOME_MAX_WIDTH, px: { xs: 2.5, md: 4 } }}>
          <Typography
            sx={{
              fontFamily: '"Sora", Arial, sans-serif',
              fontWeight: 800,
              color: '#0f172a',
              mb: 1.25,
              fontSize: { xs: '1.6rem', md: '2rem' },
            }}
          >
            Partner with EncycGames
          </Typography>
          <Typography
            sx={{
              mb: 2.5,
              fontSize: { xs: '1.05rem', md: '1.2rem' },
              color: 'text.secondary',
              maxWidth: 720,
              mx: 'auto',
              lineHeight: 1.55,
            }}
          >
            Sponsors and event organizers can register interest across Football, Cricket, Badminton and more.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/partners')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.05rem',
              px: 3,
              py: 1.1,
              fontFamily: '"Sora", Arial, sans-serif',
            }}
          >
            Sponsors & Organizers
          </Button>
        </Container>
      </Box>

      <SiteFooter />
    </Box>
  );
};

export default LandingPage;
